import { Component, OnInit, OnDestroy, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-header',
  imports: [],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class Header implements OnInit, OnDestroy {
  currentUrl = '';
  isSticky = false;
  isMobileMenuOpen = false;
  openSubmenus: Set<string> = new Set();
  private scrollOffset = 200;
  private isBrowser: boolean;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentUrl = this.router.url;
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.currentUrl = e.urlAfterRedirects || e.url || '';
      // Close mobile menu when navigating
      this.closeMobileMenu();
    });
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.onWindowScroll();
    }
  }

  ngOnDestroy() {
    // Clean up body class when component is destroyed
    if (this.isBrowser) {
      document.body.classList.remove('header-sticky');
      document.body.classList.remove('mobile-menu-open');
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.isBrowser) return;
    
    const wasSticky = this.isSticky;
    this.isSticky = window.pageYOffset > this.scrollOffset;
    
    // Add/remove body class to prevent content jump
    if (this.isSticky && !wasSticky) {
      document.body.classList.add('header-sticky');
    } else if (!this.isSticky && wasSticky) {
      document.body.classList.remove('header-sticky');
    }
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    if (!this.isBrowser) return;
    
    // Close mobile menu on window resize (desktop view)
    if (window.innerWidth > 768 && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    
    if (this.isBrowser) {
      if (this.isMobileMenuOpen) {
        document.body.classList.add('mobile-menu-open');
      } else {
        document.body.classList.remove('mobile-menu-open');
        // Close all submenus when mobile menu closes
        this.openSubmenus.clear();
      }
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    if (this.isBrowser) {
      document.body.classList.remove('mobile-menu-open');
      // Close all submenus when mobile menu closes
      this.openSubmenus.clear();
    }
  }

  toggleSubmenu(submenuKey: string, event: Event) {
    event.preventDefault();
    
    if (this.openSubmenus.has(submenuKey)) {
      this.openSubmenus.delete(submenuKey);
    } else {
      this.openSubmenus.add(submenuKey);
    }
  }

  isSubmenuOpen(submenuKey: string): boolean {
    return this.openSubmenus.has(submenuKey);
  }

  isSectionActive(section: 'about' | 'services'): boolean {
    if (!this.currentUrl) return false;
    const map: Record<string, string[]> = {
      about: ['/team', '/directors-advisors', '/timeline'],
      services: ['/platforms', '/projects', '/qa-service', '/it-services']
    };
    return map[section].some(path => this.currentUrl.startsWith(path));
  }

  isActive(path: string, exact: boolean = true): boolean {
    if (exact) {
      return this.currentUrl === path;
    }
    return this.currentUrl === path || this.currentUrl.startsWith(path + '/');
  }
}
